import { useQuery } from '@tanstack/react-query'

import { getSubsModules } from '../lib/api/Subscription-Service'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { FlaskConical, Timer } from 'lucide-react'
import { useMemo, useState } from 'react'
import SubscriptionModulesSkeleton from '../components/Subscription-Plans/SubscriptionModulesSkeleton'

const SubscriptionModules = () => {
  const { labGroupId, plan } = useParams()
  const navigate = useNavigate()
  const { searchQuery } = useOutletContext()

  const { data: moduleData, isLoading } = useQuery({
    queryKey: ['get-subscription-group-modules', labGroupId],
    queryFn: () => getSubsModules(labGroupId),
    enabled: !!labGroupId,
  })

  // Filter modules based on search query
  const filteredModules = useMemo(() => {
    if (!moduleData?.modules || !searchQuery?.trim()) {
      return moduleData?.modules || []
    }

    return moduleData.modules.filter((module) =>
      module.title.toLowerCase().includes(searchQuery.toLowerCase().trim())
    )
  }, [moduleData?.modules, searchQuery])

  function handleNavigation(module) {
    if (module?.total_labs === 0) return alert('No labs available for this module yet.')
    navigate(`/subscription/${plan}/${labGroupId}/${module._id}`)
  }

  const isModuleEmpty = filteredModules.length === 0

  if (isModuleEmpty && !isLoading) {
    return (
      <div className="w-full flex flex-col items-center justify-center text-center p-12 text-gray-500 animate-fadeIn">
        <FlaskConical className="h-12 w-12 mb-4 text-gray-400" />
        <h3 className="text-lg font-semibold mb-2">No modules found</h3>
        <p>Try adjusting your search or filter to find what you're looking for</p>
      </div>
    )
  }

  return (
    <div className="w-full mx-auto space-y-4 h-full">
      {isLoading ? (
        <SubscriptionModulesSkeleton />
      ) : (
        <section className="space-y-4">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredModules.map((module) => (
              <ModuleCard key={module._id} module={module} handleNavigation={handleNavigation} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

const ModuleCard = ({ module, handleNavigation }) => {
  return (
    <div className="w-full rounded-md bg-white p-4 flex flex-col justify-between gap-4">
      <div className="space-y-2.5">
        <figure className="w-full aspect-[296/174]">
          <img
            className="w-full h-full object-cover rounded-md"
            src={module.image || '/default-module-image.jpg'}
            alt={module.title || 'Module Image'}
          />
        </figure>

        <h2 className="text-xl font-semibold">{module.title}</h2>
      </div>
      <dd className="space-y-3.5">
        <div className="flex items-center gap-2">
          <figure className="text-gray-500 flex items-center gap-1">
            <FlaskConical className="w-4 h-4 text-slate-900" />
            <span className="text-xs 00">
              {module.total_labs} Lab{module.total_labs > 1 && 's'}
            </span>
          </figure>
          <figure className="text-gray-500 flex items-center gap-1">
            <Timer className="w-4 h-4 text-slate-900" />
            <span className="text-xs 00">
              {module.total_duration} hour{module.total_duration > 1 && 's'}
            </span>
          </figure>
        </div>

        <button
          onClick={() => {
            handleNavigation(module)
          }}
          className="w-full px-4 py-2 border rounded-md hover:bg-gray-100 duration-300">
          See All Labs
        </button>
      </dd>
    </div>
  )
}

export default SubscriptionModules
